import { type TranscribeResult, VOICE_DURATION_HEADER } from '@fos/shared/contracts/speech';
import { type AppErrorCode, isAppErrorCode } from '@fos/shared/errors';
import { ApiError, csrfHeaders, NetworkError } from '../../lib/api';

/**
 * `POST /api/voice/transcribe` (Fala 10): nagranie jako surowa treść (`content-type` = typ z MediaRecorder), czas
 * nagrania w nagłówku, CSRF jak każda mutacja. Odpowiedź: tekst do pola wiadomości (do poprawy przed wysłaniem).
 * Przeglądarka nie zapisuje nagrania — po wysłaniu blob nie jest nigdzie trzymany.
 */
export async function transcribeAudio(
  audio: Blob,
  durationMs: number,
  signal?: AbortSignal,
): Promise<TranscribeResult> {
  let response: Response;
  try {
    response = await fetch('/api/voice/transcribe', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        'content-type': audio.type || 'audio/webm',
        [VOICE_DURATION_HEADER]: String(Math.max(0, Math.round(durationMs))),
        ...csrfHeaders(),
      },
      body: audio,
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new NetworkError();
  }
  const text = await response.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!response.ok) {
    const body = (data as { error?: { code?: unknown; message?: unknown; details?: unknown } } | null)?.error;
    if (!body && response.status >= 500) throw new NetworkError();
    const code: AppErrorCode = isAppErrorCode(body?.code)
      ? body.code
      : response.status === 429
        ? 'RATE_LIMITED'
        : response.status >= 500
          ? 'INTEGRATION_UNAVAILABLE'
          : 'VALIDATION_ERROR';
    throw new ApiError({
      code,
      status: response.status,
      message: typeof body?.message === 'string' ? body.message : undefined,
      details: body?.details,
    });
  }
  return data as TranscribeResult;
}
